// src/modules/units/tyres/views/create/newtyreform.view.js
//
// Pure markup for the tyre record form, in two modes:
//   - create: blank fields (grade defaults to "New"), a required Yard the tyre
//     will belong to, the scan block, the spec, and an optional Supplier & cost
//     card. Plus a confirm panel the orchestrator uses for the duplicate-ID guard.
//   - edit:   fields prefilled from the tyre, the ID locked (it's the key), and
//     no confirm panel — the update is deliberate. The Yard IS editable here so a
//     tyre with no yard (legacy records) can be placed in one; it saves through
//     the same record upsert.
//
// Cards remain in the logical order Yard → Identity (scan) → Specification →
// Supplier & cost. On wide desktop screens CSS places Yard/Identity/Supplier in a
// left rail and Specification in the larger right rail; narrower screens fall
// back to the original single-column reading order. The scan card keeps its own
// look because scanning is the fitter's primary path. No fetching or writes here;
// dynamic select options are injected by the orchestrator after async loads.

import { esc, formatShortDate } from '../../shared/tyre-format.js';
import { icon } from '../../shared/tyre-icons.js';
import { isArchiveEligibleTyre, tyreStatusLabel, hasKnownStatus } from '../../shared/tyre-status.js';
import {
  TYRE_SIZES, TYRE_TYPES, TYRE_APPLICATIONS, TYRE_GRADES
} from '../../shared/tyre-vocab.js';

// Field config. Flags:
//   card       — which card the field renders in: yard | identity | spec | cost
//   scan       — belongs to the scan block; Enter jumps to the next scan box
//   required   — must be filled before we save
//   wide       — spans both columns of a card's grid
//   default    — seeds a starting value in create mode
//   dynamic    — a select whose options the orchestrator injects at runtime
//                (currently 'yards'); the payload builder still reads its value
//   createOnly — hidden in edit mode
//   pending    — the API does not accept this field yet (backend is adding it)

export const NEW_TYRE_FIELDS = [
  // --- yard: the site the tyre belongs to (required in both modes) -----------
  // Editable on edit as well, so a legacy tyre with no yard can be placed in one.
  {
    key: 'yardId', label: 'Yard', control: 'select', card: 'yard',
    dynamic: 'yards', required: true, wide: true,
    placeholder: 'Select a yard'
  },

  // --- identity: the codes a fitter scans -----------------------------------
  {
    key: 'externalTyreId',
    label: 'Tyre ID',
    labelNote: "your company's own tyre ID or reference number",
    control: 'text',
    card: 'identity',
    scan: true,
    required: true,
    wide: true,
    placeholder: 'Scan or enter the tyre ID'
  },
  {
    key: 'tyreRFID', label: 'RFID', control: 'text', card: 'identity',
    scan: true, pending: true, placeholder: 'Scan the RFID tag'
  },
  {
    key: 'tyreSerialNumber', label: 'Serial number', control: 'text', card: 'identity',
    scan: true, placeholder: 'Scan or enter the serial number'
  },

  // --- specification --------------------------------------------------------
  {
    key: 'tyreBrand',
    label: 'Manufacturer / brand',
    control: 'typeahead',
    card: 'spec',
    typeahead: 'manufacturer',
    placeholder: 'Start typing a manufacturer',
    helpText: 'Suggestions come from Setup → Tyre catalogue.'
  },
  {
    key: 'tyreModel',
    label: 'Model',
    control: 'typeahead',
    card: 'spec',
    typeahead: 'model',
    placeholder: 'Start typing a model'
  },
  {
    key: 'tyreTreadPattern',
    label: 'Tread pattern code',
    control: 'typeahead',
    card: 'spec',
    typeahead: 'tread',
    placeholder: 'Start typing a tread pattern'
  },
  { key: 'tyreSize', label: 'Size', control: 'select', card: 'spec', options: TYRE_SIZES },
  { key: 'tyreType', label: 'Type', control: 'select', card: 'spec', options: TYRE_TYPES },
  { key: 'tyreApplication', label: 'Application', control: 'select', card: 'spec', options: TYRE_APPLICATIONS },
  { key: 'tyreGrade', label: 'Grade', control: 'select', card: 'spec', options: TYRE_GRADES, default: 'New' },
  {
    key: 'initialTyreTreadDepth',
    label: 'Initial tread',
    //labelNote: 'the depth when brand new; for retreads, the depth when first retreaded',
    labelNote: 'tyre tread depth when brand new',
    helpText: 'Tread depth when the tyre was brand new, not when it was added to stock.',
    control: 'number',
    card: 'spec',
    step: '0.01',
    min: '0',
    unit: 'mm'
  },

  {
    key: 'minimumTreadForRetread',
    label: 'Minimum tread for retread',
    control: 'number',
    card: 'spec',
    step: '0.01',
    min: '0',
    unit: 'mm'
  },
  {
    key: 'manufacturerExpectedMileage',
    label: 'Manufacturer expected mileage',
    control: 'number',
    card: 'spec',
    step: '1',
    min: '0',
    unit: 'km'
  },
  {
    key: 'expectedKmPerMm',
    label: 'Expected km per 1 mm of usable tread',
    control: 'calculated',
    card: 'spec',
    localOnly: true
  },
  { key: 'tyreTreadDepth', label: 'Current tread', control: 'number', card: 'spec', step: '0.01', min: '0', unit: 'mm' },
  {
    key: 'tyreKm',
    label: 'Opening distance',
    labelNote: 'for existing tyres first added to stock',

    control: 'number',
    card: 'spec',
    step: '1',
    min: '0',
    unit: 'km'
  },
  { key: 'notes', label: 'Notes', control: 'textarea', card: 'spec', wide: true },

  // --- supplier & cost: all optional, never gates the save ------------------

  {
    key: 'supplierId',
    label: 'Supplier',
    control: 'select',
    card: 'cost',
    dynamic: 'suppliers',
    placeholder: 'Select a supplier'
  },
  {
    key: 'purchaseDate',
    label: 'Invoice date',
    control: 'date',
    card: 'cost'
  },
  {
    key: 'invoiceNo',
    label: 'Invoice No',
    control: 'text',
    card: 'cost',
    placeholder: 'Enter invoice number'
  },
  {
    key: 'purchaseCost',
    label: 'Purchase cost',
    labelNote: 'excl. VAT',
    control: 'number',
    card: 'cost',
    step: '0.01',
    min: '0'
  }
];

// The fields shown in a given mode — createOnly fields drop out of edit.
export function fieldsFor(mode) {
  const isEdit = mode === 'edit';
  return NEW_TYRE_FIELDS.filter((f) => !(isEdit && f.createOnly));
}

function isBlankSelectValue(value) {
  return value === null || value === undefined || value === '';
}

function control(f, value, locked) {
  const val = value ?? '';
  const id = `tmnt-${esc(f.key)}`;

  if (f.control === 'select') {
    if (f.dynamic) {
      // Options injected by the orchestrator once loaded (e.g. the yard list).
      return `<select id="${id}" class="tm-input" data-field="${esc(f.key)}" data-dynamic="${esc(f.dynamic)}"><option value="">${esc(f.placeholder || 'Select…')}</option></select>`;
    }
    const optionValues = [...(f.options || [])];
    if (!isBlankSelectValue(val) && !optionValues.some((o) => String(o) === String(val))) {
      // Preserve customer-specific catalogue values that are not in the legacy
      // frontend vocabulary yet. This is especially useful for tyre sizes.
      optionValues.unshift(String(val));
    }
    const opts = [
      `<option value="">${esc(f.placeholder || 'Select…')}</option>`
    ]
      .concat(
        optionValues.map(
          (o) =>
            `<option value="${esc(o)}"${String(o) === String(val) ? ' selected' : ''}>${esc(o)}</option>`
        )
      )
      .join('');
    return `<select id="${id}" class="tm-input" data-field="${esc(f.key)}">${opts}</select>`;
  }
  if (f.control === 'calculated') {
    return `
    <div
      id="${id}"
      class="tm-calculated-value"
      data-calculated="${esc(f.key)}"
    >
      —
    </div>`;
  }
  if (f.control === 'typeahead') {
    const listId = `tmnt-list-${esc(f.key)}`;
    const ph = f.placeholder ? ` placeholder="${esc(f.placeholder)}"` : '';
    return `<input id="${id}" type="text" class="tm-input" data-field="${esc(f.key)}" data-catalogue-typeahead="${esc(f.typeahead || f.key)}" list="${listId}" autocomplete="off" value="${esc(val)}"${ph} />` +
      `<datalist id="${listId}" data-catalogue-list="${esc(f.typeahead || f.key)}"></datalist>`;
  }
  if (f.control === 'textarea') {
    return `<textarea id="${id}" class="tm-input tm-textarea" rows="2" data-field="${esc(f.key)}">${esc(val)}</textarea>`;
  }
  if (f.control === 'number') {
    const step = f.step != null ? `step="${esc(f.step)}"` : '';
    const min = f.min != null ? `min="${esc(f.min)}"` : '';
    const input = `<input id="${id}" type="number" inputmode="decimal" class="tm-input" data-field="${esc(f.key)}" ${step} ${min} value="${esc(val)}" />`;
    return f.unit ? `<div class="tm-input-affix">${input}<span class="tm-affix tm-affix-post">${esc(f.unit)}</span></div>` : input;
  }
  if (f.control === 'date') {
    // Native date input; the record may hold an ISO datetime, so show the date part.
    const d = val ? String(val).slice(0, 10) : '';
    return `<input id="${id}" type="date" class="tm-input" data-field="${esc(f.key)}" value="${esc(d)}" />`;
  }
  // text (the only lockable control here is the ID)
  const lockCls = locked ? ' tm-input-locked' : '';
  const lockAttr = locked ? ' readonly' : '';
  const ph = (f.placeholder && !locked) ? ` placeholder="${esc(f.placeholder)}"` : '';
  // data-scan marks the boxes the orchestrator chains together with Enter.
  const scanAttr = (f.scan && !locked) ? ' data-scan' : '';
  return `<input id="${id}" type="text" class="tm-input${lockCls}" data-field="${esc(f.key)}" autocomplete="off" value="${esc(val)}"${ph}${scanAttr}${lockAttr} />`;
}

function fieldRow(f, value, locked) {
  const req = f.required
    ? '<span class="tm-req" aria-hidden="true">*</span>'
    : '';

  const labelNote = f.labelNote
    ? ` <span class="tm-field-note">(${esc(f.labelNote)})</span>`
    : '';

  const lockNote = locked
    ? ' <span class="tm-lock-note">(locked)</span>'
    : '';

  const help = f.helpText
    ? `<div class="tm-field-help">${esc(f.helpText)}</div>`
    : '';

  return `
    <div class="tm-form-row${f.wide ? ' tm-form-row-wide' : ''}">
      <label class="tm-form-label" for="tmnt-${esc(f.key)}">
        ${esc(f.label)}${req}${labelNote}${lockNote}
      </label>
      ${control(f, value, locked)}
      ${help}
    </div>`;
}

export function tyreFormHTML(mode = 'create', tyre = {}, options = {}) {
  const isEdit = mode === 'edit';
  const title = isEdit ? 'Edit tyre' : 'New tyre';
  const badge = isEdit ? 'Edit' : 'New';
  const badgeTone = isEdit ? 'primary-blue' : 'accent-green';
  const submitLabel = isEdit ? 'Save changes' : 'Submit';
  const fitTargetLabel = options.fitTargetLabel || '';
  const sub = isEdit
    ? `Update the details for tyre <b>${esc(tyre.externalTyreId || '')}</b>.`
    : fitTargetLabel
      ? `This tyre will be fitted to <b>${esc(fitTargetLabel)}</b> after it is saved.`
      : '';

  const fields = fieldsFor(mode);
  const row = (f) => {
    const value = isEdit ? (tyre[f.key] ?? '') : (f.default ?? '');
    const locked = isEdit && f.key === 'externalTyreId';
    return fieldRow(f, value, locked);
  };
  const rowsIn = (card) => fields.filter((f) => f.card === card).map(row).join('');

  // A titled card, built on the same bordered grid the spec already used. Empty
  // cards (e.g. Yard in edit mode) render nothing.
  const gridCard = (heading, rows, placementClass = '') => rows
    ? `<section class="tm-form-grid tm-form-card ${placementClass}"><h3 class="tm-form-card-title">${esc(heading)}</h3>${rows}</section>`
    : '';

  const yardCard = gridCard('Yard', rowsIn('yard'), 'tm-record-yard');

  // Identity keeps the distinct scan look — scanning is the fitter's main path.
  const scanRows = rowsIn('identity');
  const identityCard = scanRows ? `
      <section class="tm-scan tm-record-identity">
        <div class="tm-scan-head"><h3 class="tm-form-card-title tm-scan-title">Identity</h3></div>
        <div class="tm-scan-grid">${scanRows}</div>
      </section>` : '';

  const specCard = gridCard('Specification', rowsIn('spec'), 'tm-record-spec');
  const costCard = gridCard('Supplier & invoice', rowsIn('cost'), 'tm-record-cost');

  // Confirm panel — create only, and now a single use: a tyre ID that already
  // exists (the upsert guard). The sensor-occupied case left with the sensor box.
  // Rare, admin-only action, deliberately separate from normal Save/Cancel.
  // Archiving is reversible (Records → Archived tyres → Restore), so it is styled
  // as a quiet administrative control and confirmed with a plain two-step panel —
  // a typed-ID confirmation is reserved for things that cannot be undone.
  const archiveZone = isEdit && options.canArchive && isArchiveEligibleTyre(tyre) ? `
      <section class="tm-tyre-archive-zone" aria-label="Administrator actions">
        <div class="tm-tyre-archive-summary">
          <div>
            <h3>Administrator actions</h3>
            <p>Only Scrapped, Sold or Missing tyres can be archived. Archiving hides the tyre from the register, reports and exports. Its record and full history are kept, and it can be restored from <b>Records → Archived tyres</b>.</p>
          </div>
          <button type="button" class="tm-btn-archive-outline" data-archive-open>${icon('archive')}<span>Archive tyre</span></button>
        </div>
        <div class="tm-tyre-archive-confirm" data-archive-panel hidden>
          <p class="tm-tyre-archive-question" id="tm-archive-question">Archive tyre <b>${esc(tyre.externalTyreId || '')}</b>? It will no longer appear in the register, reports or exports, and its ID cannot be reused until it is restored.</p>
          <div class="tm-form-msg" data-archive-msg hidden role="alert"></div>
          <div class="tm-tyre-archive-actions">
            <button type="button" class="tm-btn-secondary" data-archive-cancel>Keep tyre</button>
            <button type="button" class="tm-btn-archive" data-archive-confirm aria-describedby="tm-archive-question">Archive tyre</button>
          </div>
        </div>
      </section>` : '';

  const confirmPanel = isEdit ? '' : `
      <div class="tm-conflict" data-conflict hidden>
        <div class="tm-conflict-msg" data-conflict-msg></div>
        <div class="tm-conflict-actions">
          <button type="button" class="tm-btn-secondary" data-conflict-cancel>Cancel</button>
          <button type="button" class="tm-btn-primary" data-conflict-confirm>Continue</button>
        </div>
      </div>`;

  return `
    <div class="tm-form tm-tyre-record-form">
      <div class="tm-form-head tm-record-form-head">
        <div class="tm-form-heading">
          <div class="tm-record-form-titleline">
            <h2 class="tm-form-title">${esc(title)}</h2>
            <span class="tm-badge tm-tone-${badgeTone}">${esc(badge)}</span>
          </div>
          <p class="tm-form-sub">${sub}</p>
        </div>
        <div class="tm-record-form-actions">
          <button type="button" class="tm-btn-secondary" data-cancel>Cancel</button>
          <button type="button" class="tm-btn-primary" data-submit>${esc(submitLabel)}</button>
        </div>
      </div>
      <div class="tm-form-msg" data-msg hidden></div>
      <div class="tm-record-form-layout">
        ${yardCard}
        ${identityCard}
        ${specCard}
        ${costCard}
      </div>
      ${confirmPanel}
      ${archiveZone}
    </div>`;
}

// ---------------------------------------------------------------------------
// Create-time guard: the entered ID belongs to an ARCHIVED tyre.
//
// The backend refuses to reuse an archived tyre's ID until it is restored, so
// "create" can never succeed here. Rather than let the save fail with a raw
// server message, the form says what the ID belongs to and offers the one thing
// that works: restore the archived tyre. It deliberately does NOT offer to apply
// the typed details to the restored record — the archived tyre is a different,
// physically retired tyre, and silently overwriting its history with a new
// tyre's spec would merge two tyres into one.
// ---------------------------------------------------------------------------

/**
 * Body of the conflict panel for an archived ID (administrators — they can see
 * the archived record and restore it).
 * @param {object} archived row from GET /api/tyre/deleted
 * @param {{ wheelContext?: boolean }} [opts] from an empty wheel: restoring does
 *   not fit the tyre, so say so.
 */
export function archivedIdConflictHTML(archived, { wheelContext = false } = {}) {
  const id = esc(archived?.externalTyreId ?? '');
  const facts = [];
  if (hasKnownStatus(archived)) facts.push(`last status <b>${esc(tyreStatusLabel(archived))}</b>`);
  if (archived?.deletedAt) facts.push(`archived ${esc(formatShortDate(archived.deletedAt))}`);
  if (archived?.deletedBy) facts.push(`by ${esc(archived.deletedBy)}`);
  const factLine = facts.length ? ` (${facts.join(', ')})` : '';
  const wheelNote = wheelContext
    ? ' Restoring does not fit it to this wheel.'
    : '';
  return (
    `Tyre ID <b>${id}</b> belongs to an archived tyre${factLine}, so it cannot be used for a new tyre. ` +
    `Restore the archived tyre to bring it back to the register with its full history, ` +
    `or cancel and enter a different ID.${wheelNote}`
  );
}

/**
 * Hint for a failed CREATE that the form could not diagnose itself — a
 * non-administrator cannot read the archived list, so the ID may belong to an
 * archived tyre without the form being able to tell. Appended to the server's
 * own message rather than replacing it, because the refusal may have another
 * cause.
 */
export const ARCHIVED_ID_HINT =
  'If this ID belonged to a tyre that was archived, an administrator can restore it from Records → Archived tyres, or you can use a different ID.';
